import type { CaseFileEntry } from "@/types";

// #303 (UC-2, Bau-Nachtrag „Artefakt-Versionen"): die ANZEIGE-Faltung der Abgaben.
//
// An der Abgabe-Mechanik ändert sich nichts — jede `step_done`-Abgabe bleibt eine eigene,
// unveränderliche Kopie am Lauf. Gleicher Dateiname = neue Fassung ist die natürliche
// Konvention des Agenten (ein explizites Etikett am Werkzeug-Schema wäre eine
// Fehlerquelle: ein Tippfehler zerrisse die Kette). Der Master zählt dafür `version`
// (1-basiert je Dateiname, Reihenfolge = Ereignis-Chronik) und markiert `latest`.
//
// Diese Datei macht daraus GRUPPEN: eine Zeile „aktuell" je Dateiname, ältere Fassungen
// aufklappbar darunter.

export interface DateiKette {
  filename: string;
  /** Die jüngste Fassung — die Zeile, die immer sichtbar ist. */
  aktuell: CaseFileEntry;
  /** Ältere Fassungen, absteigend (jüngste zuerst). Leer = nur eine Abgabe. */
  aeltere: CaseFileEntry[];
}

/**
 * Wählt die aktuelle Fassung einer Kette.
 *
 * `latest` ist die Zusage des Masters und gewinnt. Fehlt sie (älterer Master, oder eine
 * Kette, deren Abgabe-Ereignis verloren ging), entscheidet die höchste `version`; sind auch
 * die Versionen gleich, die zuletzt gelieferte Zeile. Bewusst KEINE Rückgabe von
 * `undefined`: eine Gruppe entsteht nur, wenn sie mindestens einen Eintrag hat.
 */
function aktuelleFassung(gruppe: CaseFileEntry[]): CaseFileEntry {
  const markiert = gruppe.filter((f) => f.latest === true);
  if (markiert.length === 1) return markiert[0]!;
  // Mehrere (oder keine) Markierungen: die Version entscheidet, sonst die Lieferzeit.
  const kandidaten = markiert.length > 1 ? markiert : gruppe;
  return kandidaten.reduce((a, b) => {
    const va = Number.isFinite(a.version) ? a.version : 0;
    const vb = Number.isFinite(b.version) ? b.version : 0;
    if (vb !== va) return vb > va ? b : a;
    return Date.parse(b.deliveredAt) >= Date.parse(a.deliveredAt) ? b : a;
  });
}

/**
 * Faltet die Datei-Liste eines Laufs zu Versions-Ketten.
 *
 * Gruppen-Reihenfolge: jüngste Abgabe zuerst (das ist die Frage, die jemand an einer
 * Datei-Karte hat), bei gleichem Zeitpunkt alphabetisch — damit die Sicht bei jedem Poll
 * dieselbe bleibt.
 */
export function falteDateiVersionen(files: readonly CaseFileEntry[]): DateiKette[] {
  // Fremdformat-Härtung: was der Server schickt, ist eine Annahme. Eine Nicht-Liste (oder
  // ein Eintrag ohne Namen) darf die Karte nicht fällen — sie zeigt dann eben nichts.
  if (!Array.isArray(files)) return [];
  const gruppen = new Map<string, CaseFileEntry[]>();
  for (const f of files) {
    if (!f || typeof f.filename !== "string") continue;
    const bisher = gruppen.get(f.filename);
    if (bisher) bisher.push(f);
    else gruppen.set(f.filename, [f]);
  }

  const ketten: DateiKette[] = [];
  for (const [filename, gruppe] of gruppen) {
    const aktuell = aktuelleFassung(gruppe);
    const aeltere = gruppe
      .filter((f) => f !== aktuell)
      .sort((a, b) => (b.version ?? 0) - (a.version ?? 0) || Date.parse(b.deliveredAt) - Date.parse(a.deliveredAt));
    ketten.push({ filename, aktuell, aeltere });
  }

  return ketten.sort(
    (a, b) =>
      Date.parse(b.aktuell.deliveredAt) - Date.parse(a.aktuell.deliveredAt) ||
      a.filename.localeCompare(b.filename),
  );
}

/** Dateigrösse menschenlesbar. Ein `Content-Length` in Bytes sagt niemandem etwas. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
